import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, AudioLines, Info, Loader2 } from 'lucide-react';
import Button from '@components/atoms/ui-kit/button.tsx';
import SelectInput from '@components/atoms/ui-kit/select-input.tsx';
import { SegmentedControl } from '@components/atoms/ui-kit/segmented-control.tsx';
import { useThemeContext } from '@utils/providers/theme-context.tsx';
import { useContributionModels } from './use-contribution-models.ts';

export type ContributionMode = 'only' | 'up-to' | 'whole';

export type ContributionNote = { tone: 'info' | 'warn'; text: string };

type Props = {
  models: ReturnType<typeof useContributionModels>;
  mode: ContributionMode;
  onModeChange: (mode: ContributionMode) => void;
  /** One line saying what the click will add, e.g. "Adds 1m 58s to X". */
  summary?: string;
  notes: ContributionNote[];
  /** The segments that will be sent. Ignored in whole-file mode. */
  children: ReactNode;
  canSubmit: boolean;
  busy: boolean;
  onSubmit: () => void;
  error?: string;
};

/**
 * The "Add to training data" form, shared by the caption and transcript
 * editors so both look and behave the same.
 *
 * Laid out for a narrow, tall dialog: the segment list gets the height, and
 * everything the customer should know but need not read twice sits in small
 * notes below it instead of paragraphs between the controls.
 */
export const AddToTrainingForm = ({
  models,
  mode,
  onModeChange,
  summary,
  notes,
  children,
  canSubmit,
  busy,
  onSubmit,
  error
}: Props) => {
  const { t } = useTranslation();
  const { getColour, theme } = useThemeContext();
  const isDark = theme === 'dark';
  const muted = getColour('inactiveText');
  const border = getColour('border');
  const accent = getColour('activeText');

  if (models.isLoading) {
    return (
      <div
        className="flex items-center gap-2 text-sm py-10 justify-center"
        style={{ color: muted }}
      >
        <Loader2 size={16} className="animate-spin" aria-hidden="true" />
        <span>{t('loading-models')}</span>
      </div>
    );
  }

  const tone = {
    info: {
      background: getColour('brandSoft'),
      color: getColour('normalText'),
      icon: accent
    },
    warn: {
      background: isDark ? 'rgb(66 32 6 / 0.6)' : 'rgb(254 243 199)',
      color: isDark ? 'rgb(253 230 138)' : 'rgb(120 53 15)',
      icon: isDark ? 'rgb(251 191 36)' : 'rgb(217 119 6)'
    }
  };

  return (
    <div
      className="flex flex-col gap-3 text-sm"
      style={{ color: getColour('normalText') }}
    >
      <label className="flex flex-col gap-1">
        <span
          className="text-[11px] font-medium uppercase tracking-wide"
          style={{ color: muted }}
        >
          {t('model')}
        </span>
        {models.eligibleModels.length === 0 ? (
          <span className="text-xs" style={{ color: muted }}>
            {models.allMidTraining ? (
              t('model-mid-training')
            ) : (
              <>
                {t('no-trainable-models-for-language')}{' '}
                <a
                  href="/custom-models"
                  className="underline"
                  style={{ color: accent }}
                  target="_blank"
                  rel="noreferrer"
                >
                  {t('create-one')}
                </a>
              </>
            )}
          </span>
        ) : (
          <SelectInput
            options={models.eligibleModels.map(m => ({
              label: m.name,
              value: m.id
            }))}
            value={models.selectedModelId ?? undefined}
            onChange={opt => {
              if (opt) models.selectModel(Number(opt.value));
            }}
            size="small"
            isSearchable={false}
          />
        )}
      </label>

      <SegmentedControl<ContributionMode>
        value={mode}
        onChange={onModeChange}
        ariaLabel={t('add-to-training-data')}
        size="md"
        options={[
          { value: 'only', label: t('training-clip-only-this') },
          { value: 'up-to', label: t('training-clip-up-to-this') },
          { value: 'whole', label: t('training-clip-whole-file') }
        ]}
      />

      <div
        className="h-72 overflow-y-auto rounded-lg border p-1.5"
        style={{ borderColor: border }}
      >
        {mode === 'whole' ? (
          <div
            className="h-full flex flex-col items-center justify-center gap-2 px-6 text-center"
            style={{ color: muted }}
          >
            <AudioLines
              size={28}
              style={{ color: accent }}
              aria-hidden="true"
            />
            <p className="text-xs leading-relaxed">
              {t('contribution-whole-hint')}
            </p>
          </div>
        ) : (
          <div className="space-y-1">{children}</div>
        )}
      </div>

      {notes.length > 0 && (
        <ul className="flex flex-col gap-1.5">
          {notes.map(note => {
            const Icon = note.tone === 'warn' ? AlertTriangle : Info;
            const colours = tone[note.tone];
            return (
              <li
                key={note.text}
                className="flex items-start gap-1.5 rounded-md px-2 py-1.5 text-[11px] leading-snug"
                style={{
                  backgroundColor: colours.background,
                  color: colours.color
                }}
              >
                <Icon
                  size={12}
                  className="mt-px shrink-0"
                  style={{ color: colours.icon }}
                  aria-hidden="true"
                />
                <span>{note.text}</span>
              </li>
            );
          })}
        </ul>
      )}

      <div className="flex items-center justify-between gap-3 pt-1">
        <span className="text-[11px] leading-snug" style={{ color: muted }}>
          {summary ?? t('contribution-no-training-started-short')}
        </span>
        <Button
          variant="primary"
          size="small"
          label={
            mode === 'whole'
              ? t('contribute-whole-file')
              : t('contribute-action')
          }
          disabled={!canSubmit}
          loading={busy}
          onClick={onSubmit}
          className="shrink-0"
        />
      </div>

      {error && (
        <p className="text-xs" style={{ color: getColour('danger') }}>
          {error}
        </p>
      )}
    </div>
  );
};
